import { apiClient } from '@core/api/ApiClientFactory';

// ============ TYPES ============

export interface AssignedShop {
    id: string;
    name: string;
    configApplied: boolean;
}

export interface UnassignedShop {
    id: string;
    name: string;
}

export interface LogicServiceVersion {
    version: number;
    uploadDateTime: string;
    uploadedBy: string;
    changeDescription: string;
    configJson: string;
}

export interface LogicServiceLayout {
    id: string;
    name: string;
    version: number;
    uploadDateTime: string;
    uploadedBy: string;
    changes: string;
    configJson: string;
    assignedShops: AssignedShop[];
    history: LogicServiceVersion[];
}

export interface CreateUpdateLayoutData {
    name: string;
    configJson: string;
    description: string;
}

interface LogicServiceConfigWithShops {
    id: string;
    name: string;
    versions: LogicServiceVersion[];
    assignedShops: AssignedShop[];
}

interface GetAllResponse {
    logicServiceConfigWithShops: LogicServiceConfigWithShops[];
    unassignedShops: string[];
}

function mapToLayout(config: LogicServiceConfigWithShops): LogicServiceLayout {
    const sorted = [...config.versions].sort(
        (a, b) => new Date(b.uploadDateTime).getTime() - new Date(a.uploadDateTime).getTime()
    );
    const latest = sorted[0];

    return {
        id: config.id,
        name: config.name,
        version: latest?.version ?? 1,
        uploadDateTime: latest?.uploadDateTime ?? new Date().toISOString(),
        uploadedBy: latest?.uploadedBy ?? '',
        changes: latest?.changeDescription ?? '',
        configJson: latest?.configJson ?? '',
        assignedShops: config.assignedShops,
        history: sorted.slice(1)
    };
}

// ============ SERVICE ============

export const logicServiceManagementService = {
    async loadAll(): Promise<{ layouts: LogicServiceLayout[]; unassignedShops: UnassignedShop[] }> {
        const response = await apiClient.get<GetAllResponse>('LogicService');
        return {
            layouts: response.data.logicServiceConfigWithShops.map(mapToLayout),
            unassignedShops: response.data.unassignedShops.map((name) => ({ id: name, name }))
        };
    },

    async createLayout(data: CreateUpdateLayoutData): Promise<LogicServiceLayout> {
        const createResponse = await apiClient.post<string>('LogicService', {
            name: data.name,
            description: data.description,
            configJson: data.configJson
        });
        const id = createResponse.data;
        const detailResponse = await apiClient.get<LogicServiceConfigWithShops>(`LogicService/${id}`);
        return mapToLayout(detailResponse.data);
    },

    async updateLayout(existing: LogicServiceLayout, data: CreateUpdateLayoutData): Promise<LogicServiceLayout> {
        await apiClient.put<string>(`LogicService/${existing.id}`, {
            id: existing.id,
            name: data.name,
            description: data.description,
            configJson: data.configJson
        });
        const detailResponse = await apiClient.get<LogicServiceConfigWithShops>(`LogicService/${existing.id}`);
        return mapToLayout(detailResponse.data);
    },

    async deleteLayout(id: string): Promise<void> {
        await apiClient.delete(`LogicService/${id}`);
    },

    downloadLayout(layout: LogicServiceLayout): void {
        const blob = new Blob([layout.configJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${layout.name}_v${layout.version}.json`;
        a.click();
        URL.revokeObjectURL(url);
    },

    downloadHistoryVersion(layoutName: string, version: LogicServiceVersion): void {
        const blob = new Blob([version.configJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${layoutName}_v${version.version}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
};
